import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAdminDashboard, listAdminOrders } from '../../../services/adminApi';
import { formatEuros } from '../catalog/productForm';
import { Tag } from '../catalog/ui';
import { adminPath } from '../shell/adminNav';
import { argent, TON_GROUPE } from '../orders/OrderDrawer';
import { Delta, Kpi, PeriodSwitch, VisitorsChart } from './statsParts';
import OnlineCard from './OnlineCard';
import '../catalog/catalog.css';
import '../books/people.css';
import './stats.css';

// Tableau de bord : les six chiffres du moment (avec la variation sur la période précédente), les visiteurs des 14 derniers jours,
// l'entonnoir de la visite à la commande, les dernières commandes. Les alertes (commande en échec, incident, nouveaux visiteurs)
// restent au-dessus, dans la page. Définitions des chiffres : backend/services/admin/adminStats.js.

const ETAPES = ['visitors', 'accounts', 'booksStarted', 'booksFinished', 'ordered'];

export default function DashboardSection() {
  const { t, i18n } = useTranslation('admin');
  const [params, setParams] = useSearchParams();
  const jours = [1, 7, 30].includes(Number(params.get('jours'))) ? Number(params.get('jours')) : 7;
  const [data, setData] = useState(null);
  const [commandes, setCommandes] = useState(null);
  const [erreur, setErreur] = useState('');
  const derniere = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++derniere.current;
    try {
      const [stats, liste] = await Promise.all([getAdminDashboard({ jours }), listAdminOrders({}).catch(() => null)]);
      if (numero === derniere.current) { setData(stats); setCommandes(liste ? liste.orders.slice(0, 4) : []); setErreur(''); }
    } catch (err) {
      if (numero === derniere.current) setErreur(err?.message || t('stats.loadFailed'));
    }
  }, [jours, t]);
  useEffect(() => { setData(null); charger(); }, [charger]);

  const k = data && data.kpis;
  const marge = k && k.marginCents;
  const pourcentMarge = k && k.revenueCents.value > 0 ? Math.round((marge.value / k.revenueCents.value) * 100) : null;
  const f = data && data.funnel;

  return (
    <>
      <div className="adm-actions">
        <PeriodSwitch value={jours} onChange={(p) => setParams(p === 7 ? {} : { jours: String(p) }, { replace: true })} />
        <span className="adm-note">{t('stats.vsPrevious', { days: jours })}</span>
      </div>

      <OnlineCard />

      {erreur && <p className="adm-error" role="alert">{erreur}</p>}
      {!data && !erreur && <p className="adm-state" role="status">{t('catalog.common.loading')}</p>}

      {data && (
        <>
          <div className="adm-kpis adm-kpis-6">
            <Kpi label={t('stats.kpi.visitors')} value={k.visitors.value}><Delta valeur={k.visitors.value} precedent={k.visitors.previous} /></Kpi>
            <Kpi label={t('stats.kpi.booksStarted')} value={k.booksStarted.value}>
              <small>{t('stats.kpi.booksStartedNote', { count: k.booksStarted.byAnonymous })}</small>
            </Kpi>
            <Kpi label={t('stats.kpi.booksFinished')} value={k.booksFinished.value}>
              <small>{k.booksStarted.value ? t('stats.kpi.booksFinishedNote', { percent: Math.round((k.booksFinished.value / k.booksStarted.value) * 100) }) : <Delta valeur={k.booksFinished.value} precedent={k.booksFinished.previous} />}</small>
            </Kpi>
            <Kpi label={t('stats.kpi.orders')} value={k.orders.value} tone={k.orders.failed ? 'is-alert' : ''}>
              {k.orders.failed > 0
                ? <small className="is-down"><Link to={`${adminPath('orders')}?statut=echec`}>{t('stats.kpi.ordersFailed', { count: k.orders.failed })}</Link></small>
                : <Delta valeur={k.orders.value} precedent={k.orders.previous} />}
            </Kpi>
            <Kpi label={t('stats.kpi.revenue')} value={formatEuros(k.revenueCents.value)}><small>{t('stats.kpi.revenueNote')}</small></Kpi>
            <Kpi label={t('stats.kpi.margin')} value={formatEuros(marge.value)}>
              <small>{pourcentMarge === null ? t('stats.kpi.marginNone') : t('stats.kpi.marginNote', { percent: pourcentMarge })}{marge.unknownCost > 0 ? ` · ${t('stats.kpi.marginUnknown', { count: marge.unknownCost })}` : ''}</small>
            </Kpi>
          </div>

          <div className="adm-cols">
            <section className="adm-card">
              <h2>{t('stats.visitorsByDay')}</h2>
              <p className="adm-note">{t('stats.visitorsByDayNote')}</p>
              <VisitorsChart days={data.visitorsByDay} />
            </section>
            <section className="adm-card">
              <h2>{t('stats.funnel.title')}</h2>
              <p className="adm-note">{t('stats.funnel.note', { days: jours })}</p>
              <ul className="adm-funnel">
                {ETAPES.map((e, i) => {
                  const precedent = i > 0 ? f[ETAPES[i - 1]] : null;
                  return (
                    <li key={e}>
                      <span className="adm-bar-label">{t(`stats.funnel.${e}`)}</span>
                      <span className="adm-bar-track"><i style={{ width: `${f.visitors > 0 ? Math.max(f[e] > 0 ? 2 : 0, Math.round((f[e] / f.visitors) * 100)) : 0}%` }} /></span>
                      <b>{f[e]}</b>
                      {precedent > 0 && <small>{t('stats.funnel.ofPrevious', { percent: Math.round((f[e] / precedent) * 100) })}</small>}
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>

          {(data.truncated || data.legacyViewsWithoutVisitor > 0) && (
            <p className="adm-note">{data.truncated ? t('stats.truncated') : t('stats.legacy', { count: data.legacyViewsWithoutVisitor })}</p>
          )}
        </>
      )}

      <section>
        <div className="adm-actions">
          <h2>{t('stats.latestOrders')}</h2>
          <span className="adm-sp" />
          <Link className="btn btn-outline btn-sm" to={adminPath('orders')}>{t('stats.allOrders')}</Link>
        </div>
        {commandes && commandes.length === 0 && <p className="adm-note">{t('orders.empty')}</p>}
        {commandes && commandes.length > 0 && (
          <div className="adm-tbl">
            <table>
              <thead><tr><th>{t('orders.cols.order')}</th><th>{t('orders.cols.customer')}</th><th>{t('orders.cols.provider')}</th><th>{t('orders.cols.status')}</th><th className="num">{t('orders.cols.total')}</th></tr></thead>
              <tbody>
                {commandes.map((o) => (
                  <tr key={o.id}>
                    <td><Link to={`${adminPath('orders')}?commande=${o.id}`}><b>{o.number || o.id.slice(0, 8)}</b></Link>{o.mode === 'test' && <> <Tag tone="warn">{t('orders.modes.testTag')}</Tag></>}<small>{new Date(o.createdAt).toLocaleDateString(i18n.language)}</small></td>
                    <td>{o.customer.name || '—'}<small>{o.customer.country || ''}</small></td>
                    <td>{t(`orders.providers.${o.provider || 'aucun'}`, { defaultValue: o.provider })}</td>
                    <td><Tag tone={TON_GROUPE[o.group]}>{t(`orders.groups.${o.group}`)}</Tag></td>
                    <td className="num">{argent(o.total.cents, o.total.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
